import React, { useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/browser';
import { currentUser } from 'js/utils/user';
import { getReactions } from 'js/social/api/reaction_api';
import { ResourceType, UserLike } from 'js/social/shared/types';
import { UserListMenu } from './UserListMenu';

type Props = {
  resourceId: number | string;
  resourceType: ResourceType;
  liked: boolean;
};

const viewer: UserLike | null = currentUser && {
  affiliation: currentUser.department?.university?.name ?? '',
  displayName:
    currentUser.display_name ||
    `${currentUser.first_name} ${currentUser.last_name}`,
  id: currentUser.id,
  photo: currentUser.photo,
  profileUrl: currentUser.url,
};

export const LikesMenu: React.FC<React.PropsWithChildren<Props>> = ({
  resourceId,
  resourceType,
  liked,
  children,
}) => {
  const [fetched, setFetched] = useState<UserLike[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!visible || fetched) return;

    setLoading(true);
    getReactions(resourceId, resourceType)
      .then((reactions) => setFetched(reactions.like))
      .catch(captureException)
      .finally(() => setLoading(false));
  }, [resourceId, resourceType, fetched, visible]);

  // Filtering before appending keeps an unlike, and a re-like, correct; the null
  // check keeps a failed fetch from implying the viewer is the only liker.
  const users = useMemo(() => {
    if (!fetched || !viewer) return fetched ?? [];
    const others = fetched.filter((user) => user.id !== viewer.id);
    return liked ? [...others, viewer] : others;
  }, [fetched, liked]);

  return (
    <UserListMenu
      users={users}
      loading={loading}
      menuProps={{ onChange: setVisible }}
    >
      {children}
    </UserListMenu>
  );
};
